import { CircularProgress, Stack, Typography } from "@mui/material";
import { AccountPlanCardMenu } from "./AccountPlanCardMenu";
import { CustomButton } from "../CustomButton";
import { Link } from "react-router-dom";
import { AccountPlanStatus, AppRoutes } from "../../../types/enums";
import { ExportAccountsPlanPDFBtn, UpdateAccountsPlanBtn } from "../buttons";
import { TwoPersonIcon } from "../../images/TwoPersonIcon";
import { GetAccountPlanBtn } from "./GetAccountPlanBtn";
import { useAppSelector } from "../../../hooks";
import { useEffect, useState } from "react";
import { NewBlock } from "../../company/reports";
import { ExportPlaybookBtn } from "../buttons/ExportPlaybookBtn";
import { formattedDate } from "../../../utils/formatDate";
import { AlertInTriangleIcon } from "../../images";
import { ReuseTooltip } from "../ReuseTooltip";

export const AccountPlanCard = ({
  id,
  campaign_id,
  name,
  updatedAt,
  isGrid,
  isNeedsUpdate,
  status,
  totalContacts,
  isNew,
  isPlaybook = false,
  isEditor,
}: {
  id: string;
  campaign_id: string;
  name: string;
  updatedAt: string;
  isGrid: boolean;
  isNeedsUpdate: boolean;
  status: AccountPlanStatus;
  totalContacts?: number;
  isNew: boolean;
  isPlaybook?: boolean;
  isEditor: boolean;
}) => {
  const [currentStatus, setCurrentStatus] = useState<AccountPlanStatus>(status);
  const updatedStatus = useAppSelector(
    (state) => state.company.updatedStatus[id],
  );

  useEffect(() => {
    setCurrentStatus(updatedStatus || status);
  }, [status, updatedStatus]);

  const btnHeight = isGrid ? "32px" : "42px";
  const btnPadding = isGrid ? "4px" : "8px";
  const isCompleted = currentStatus === AccountPlanStatus.completed;
  const isFailed = currentStatus === AccountPlanStatus.failed;
  const isInProgress = currentStatus === AccountPlanStatus.in_progress;
  const isCurrentNeedUpdates =
    isNeedsUpdate && updatedStatus !== AccountPlanStatus.completed;

  return (
    <Stack
      sx={{
        flexDirection: isGrid ? "column" : "row",
        flexGrow: 1,
        padding: `${isGrid ? "16px" : "12px"} 16px`,
        border: "1px solid #DEDEDE",
        borderRadius: "8px",
        justifyContent: "space-between",
        alignItems: isGrid ? undefined : "center",
        gap: "16px",
      }}
    >
      <Stack gap={"4px"}>
        {isGrid ? (
          <>
            <Stack flexDirection={"row"} gap={"4px"} alignItems={"center"}>
              {isNew && <NewBlock />}
              <Stack
                flexDirection={"row"}
                gap={"8px"}
                alignItems={"center"}
                justifyContent={"space-between"}
                flexGrow={1}
              >
                <Typography
                  variant="body2"
                  color={"custom.textDarkBlue"}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {isPlaybook
                    ? "BDR Engagement Playbook for"
                    : "Account Plan for"}
                </Typography>
                {isFailed && (
                  <ReuseTooltip
                    text={
                      "You’ve already attempted to generate playbook for this prospect."
                    }
                    hideChildren={false}
                    maxWidth={"184px"}
                  >
                    <AlertInTriangleIcon />
                  </ReuseTooltip>
                )}
              </Stack>
            </Stack>
            <Typography
              variant="subtitle1"
              fontWeight={500}
              color={"custom.textDarkBlue"}
            >
              {name}
            </Typography>
          </>
        ) : (
          <Stack flexDirection={"row"} gap={"4px"} alignItems={"center"}>
            {isNew && <NewBlock />}
            <Typography variant="subtitle1" color={"custom.textDarkBlue"}>
              {isPlaybook
                ? "BDR Engagement Playbook for "
                : "Account Plan for "}
              <span style={{ fontWeight: 500 }}>{name}</span>
            </Typography>
          </Stack>
        )}

        {isCompleted ? (
          <>
            <Typography
              variant="body2"
              sx={{ color: "#797979", paddingTop: "4px" }}
            >
              Last Updated: {updatedAt ? formattedDate(updatedAt) : "Today"}
            </Typography>
          </>
        ) : (
          <>
            {!isPlaybook && (
              <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
                <TwoPersonIcon
                  color={"#232323"}
                  secondColor={"#232323"}
                  width={"12px"}
                  height={"12px"}
                />
                <Typography
                  component="span"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#232323",
                    textWrap: "wrap",
                  }}
                >
                  {totalContacts} Targeted Contacts
                </Typography>
              </Stack>
            )}
          </>
        )}
      </Stack>
      {isCompleted ? (
        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
            <Link
              to={`/${campaign_id}${isPlaybook ? AppRoutes.PLAYBOOK : AppRoutes.ACCOUNT_PLAN}/${id}`}
            >
              <CustomButton
                customSize={"fit"}
                customColor="#1C3C6C"
                sx={{ height: btnHeight }}
              >
                Open
              </CustomButton>
            </Link>
            {isPlaybook ? (
              <ExportPlaybookBtn
                member_id={id}
                campaign_id={campaign_id}
                btnHeight={btnHeight}
                btnPadding={btnPadding}
              />
            ) : (
              <ExportAccountsPlanPDFBtn
                account_id={id}
                campaign_id={campaign_id}
                btnHeight={btnHeight}
                isSingleAccount={true}
                hideButton={false}
                btnPadding={btnPadding}
              />
            )}

            {isEditor && isCurrentNeedUpdates && !isPlaybook && (
              <UpdateAccountsPlanBtn
                campaign_id={campaign_id!}
                hideButton={false}
                isSingleAccount={true}
                account_id={id}
                btnHeight={btnHeight}
                btnPadding={btnPadding}
              />
            )}
          </Stack>

          {isEditor && (
            <AccountPlanCardMenu
              name={name}
              isPlaybook={isPlaybook}
              id={id}
              campaign_id={campaign_id}
              onSuccess={() => setCurrentStatus(AccountPlanStatus.ready)}
            />
          )}
        </Stack>
      ) : (
        <>
          {isInProgress ? (
            <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
              <CircularProgress size={24} sx={{ color: "inherit" }} />
              <Typography variant="button" sx={{ color: "#1F2932" }}>
                {isPlaybook ? "Generating Playbook" : "Generating Account Plan"}
              </Typography>
            </Stack>
          ) : (
            <Stack flexDirection={"row"} gap={"16px"} alignItems={"center"}>
              {isFailed && !isGrid && (
                <ReuseTooltip
                  text={
                    "You’ve already attempted to generate playbook for this prospect."
                  }
                  hideChildren={false}
                  maxWidth={"184px"}
                >
                  <AlertInTriangleIcon />
                </ReuseTooltip>
              )}
              <GetAccountPlanBtn
                id={id}
                campaign_id={campaign_id}
                fullWidth={isGrid}
                isPlaybook={isPlaybook}
              />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};
